import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import progressGif from "../../images/progress.gif";
import Pmgkay_FPS from "./Pmgkay_FPS"; 

const Pmgkay_Afso = ({ distCode, distName, month, year, singleRowOnly = false }) => {
  const [data, setData] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshTime, setRefreshTime] = useState("");
  const [selectedAfso, setSelectedAfso] = useState(null); // To store clicked AFSO row

  const headerStyle = {
    backgroundColor: "black",
    color: "white",
    textAlign: "center",
    padding: "3px 5px",
    fontSize: "14px",
  };

  const cellStyle = {
    padding: "3px 5px",
    textAlign: "center",
    fontSize: "14px",
  };

  const getMonthName = (monthNumber) => {
    const monthNames = [
      "January","February","March","April","May","June",
      "July","August","September","October","November","December"
    ];
    return monthNames[monthNumber - 1] || "";
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!distCode) return;
      setLoading(true);
      try {
        const schemeRes = await axios.get(`${process.env.REACT_APP_URL_SIDE}/api/pmgkay/schemes`);
        const schemeList = schemeRes.data.map((s) => ({
          ...s,
          keys: ["cards", "rice", "wheat"].map((k) => `${k}_${s.schemeId}`),
        }));
        setSchemes(schemeList);

        const summaryRes = await axios.post(`${process.env.REACT_APP_URL_SIDE}/api/pmgkay/afsoSummary`, {
          distCode,
          month,
          year,
          schemeIds: schemeList.map((s) => s.schemeId),
          singleRowOnly,
        });

        const summaryData = summaryRes.data.map((row) => ({
          ...row,
          ...row.dynamicFields,
        }));

        setData(summaryData);

        const now = new Date();
        const formatDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        setRefreshTime(formatDate);

      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [distCode, month, year, singleRowOnly]);

  const handleDownload = () => {
    if (!data.length) return;

    const sheetData = [];

    const firstHeader = ["Sl.No", "AFSO", "Total Cards"];
    schemes.forEach(s => firstHeader.push(...[`${s.schemeDesc} - Cards`, `${s.schemeDesc} - Rice Sale`, `${s.schemeDesc} - Wheat Sale`]));
    firstHeader.push("Total Cards", "Availed %", "Total Rice", "Total Wheat");
    sheetData.push(firstHeader);

    data.forEach((row, idx) => {
      const rowData = [idx + 1, row.afsoNameEn || "-", row.totalCards ?? 0];
      schemes.forEach(s => {
        rowData.push(row[s.keys[0]] ?? 0, (row[s.keys[1]] ?? 0).toFixed(3), (row[s.keys[2]] ?? 0).toFixed(3));
      });
      rowData.push(
        row.availPmgkayCards ?? 0,
        row.totalCards ? ((row.availPmgkayCards / row.totalCards) * 100).toFixed(2) : "0.00",
        (row.pmgkaySale ?? 0).toFixed(3),
        (row.pmgkayWheatSale ?? 0).toFixed(3)
      );
      sheetData.push(rowData);
    });

    const totalsRow = ["Total", "", data.reduce((sum, row) => sum + (row.totalCards || 0), 0)];
    schemes.forEach(s => {
      totalsRow.push(
        data.reduce((sum, row) => sum + (row[s.keys[0]] || 0), 0),
        data.reduce((sum, row) => sum + (row[s.keys[1]] || 0), 0).toFixed(3),
        data.reduce((sum, row) => sum + (row[s.keys[2]] || 0), 0).toFixed(3)
      );
    });
    totalsRow.push(
      data.reduce((sum, row) => sum + (row.availPmgkayCards || 0), 0),
      (data.reduce((sum, row) => sum + (row.availPmgkayCards || 0), 0) / data.reduce((sum, row) => sum + (row.totalCards || 0), 0) * 100),
      data.reduce((sum, row) => sum + (row.pmgkaySale || 0), 0).toFixed(3),
      data.reduce((sum, row) => sum + (row.pmgkayWheatSale || 0), 0).toFixed(3)
    );
    sheetData.push(totalsRow);

    sheetData.push([`Refresh Time: ${refreshTime}`]);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(wb, ws, "PMGKAY Data");
    XLSX.writeFile(wb, `PMGKAY_${distName}_${month}_${year}.xlsx`);
  };

  if (!distCode) return null;
  if (loading) return (
    <div style={{ textAlign: "center", marginTop: "20px" }}>
      <img src={progressGif} alt="Loading..." width="250" height="15" />
    </div>
  );

  return (
    <div style={{ paddingBottom: "300px" }}>
      <table border="1" cellPadding="5" cellSpacing="0" width="70%" align="center">
        <thead>
          <tr>
            <th colSpan={3 + schemes.length * 3 + 4} style={{ ...headerStyle, fontSize: "18px", padding: "10px" }}>
              {distName} PMGKAY Details for {getMonthName(Number(month))} '{year}
            </th>
          </tr>
          <tr>
            <th rowSpan="3" style={headerStyle}>Sl.No</th>
            <th rowSpan="3" style={headerStyle}>AFSO</th>
            <th rowSpan="3" style={headerStyle}>Total<br/>Cards</th>
            <th colSpan={schemes.length * 3 + 4} style={headerStyle}>Availed</th>
          </tr>
          <tr>
            {schemes.map(s => <th key={s.schemeId} colSpan="3" style={headerStyle}>{s.schemeDesc}</th>)}
            <th colSpan="4" style={headerStyle}>Total</th>
          </tr>
          <tr>
            {schemes.map(s => (
              <React.Fragment key={s.schemeId}>
                <th style={headerStyle}>Cards</th>
                <th style={headerStyle}>Rice Sale<br/> (in Kgs)</th>
                <th style={headerStyle}>Wheat Sale<br/> (in Kgs)</th>
              </React.Fragment>
            ))}
            <th style={headerStyle}>Cards</th>
            <th style={headerStyle}>Availed %</th>
            <th style={headerStyle}>Rice Sale<br/> (in Kgs)</th>
            <th style={headerStyle}>Wheat Sale<br/> (in Kgs)</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={idx}>
              <td style={cellStyle}>{idx + 1}</td>
              <td
                style={{
                  ...cellStyle,
                  textDecoration: "underline",
                  color: "black",
                  cursor: "pointer",
                }}
                onClick={() => setSelectedAfso(row)}
                onMouseEnter={(e) => e.target.style.color = "red"}
                onMouseLeave={(e) => e.target.style.color = "black"}
              >
                {row.afsoNameEn || "-"}
              </td>
              <td style={cellStyle}>{row.totalCards ?? 0}</td>
              {schemes.map(s =>
                s.keys.map(k => <td key={k} style={cellStyle}>{(k.startsWith("rice_") || k.startsWith("wheat_") ? (row[k] ?? 0).toFixed(3) : row[k] ?? 0)}</td>)
              )}
              <td style={cellStyle}>{row.availPmgkayCards ?? 0}</td>
              <td style={cellStyle}>{row.totalCards ? ((row.availPmgkayCards / row.totalCards) * 100).toFixed(2) : "0.00"}</td>
              <td style={cellStyle}>{(row.pmgkaySale ?? 0).toFixed(3)}</td>
              <td style={cellStyle}>{(row.pmgkayWheatSale ?? 0).toFixed(3)}</td>
            </tr>
          ))}

          <tr style={{ backgroundColor: "black", color: "white", fontWeight: "bold" }}>
            <td colSpan="2" style={{ textAlign: "center" }}>Total</td>
            <td style={cellStyle}>{data.reduce((sum, row) => sum + (row.totalCards || 0), 0)}</td>
            {schemes.map(s =>
              s.keys.map(k =>
                <td key={k} style={cellStyle}>
                  {(k.startsWith("rice_") || k.startsWith("wheat_") ? data.reduce((sum, row) => sum + (row[k] || 0), 0).toFixed(3) : data.reduce((sum, row) => sum + (row[k] || 0), 0))}
                </td>
              )
            )}
            <td style={cellStyle}>{data.reduce((sum, row) => sum + (row.availPmgkayCards || 0), 0)}</td>
            <td style={cellStyle}>{(data.reduce((sum, row) => sum + (row.availPmgkayCards || 0), 0) / data.reduce((sum, row) => sum + (row.totalCards || 0), 0) * 100)}</td>
            <td style={cellStyle}>{data.reduce((sum, row) => sum + (row.pmgkaySale || 0), 0).toFixed(3)}</td>
            <td style={cellStyle}>{data.reduce((sum, row) => sum + (row.pmgkayWheatSale || 0), 0).toFixed(3)}</td>
          </tr>

          <tr style={{ backgroundColor: "white", color: "black" }}>
            <td colSpan={3 + schemes.length * 3 + 4} style={{ ...cellStyle, textAlign: "right" }}>
              Refresh Time: {refreshTime}
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ textAlign: "center", marginTop: "px" }}>
        <button
          onClick={handleDownload}
          style={{
            padding: "6px 20px",
            fontSize: "14px",
            backgroundColor: "black",
            color: "white",
            border: "none",
            borderRadius: "20px",
            cursor: "pointer"
          }}
        >
          Data to Excel
        </button>
      </div>

      {selectedAfso && (
        <div style={{ marginTop: "50px" }}>
          <Pmgkay_FPS
            distCode={selectedAfso.afsoCode}
            distName={`${distName} / ${selectedAfso.afsoNameEn}`} // Dynamic header
            month={month}
            year={year}
            singleRowOnly={singleRowOnly}
          />
        </div>
      )}
    </div>
  );
};

export default Pmgkay_Afso;
